import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { business } from "@/data/siteConfig";
import { MaskReveal } from "@/components/Reveal";
import { CallButton } from "@/components/CallButton";
import { MediaPlaceholder } from "@/components/MediaPlaceholder";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yMedia = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 45]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      className="grain relative overflow-hidden bg-ink pt-[72px]"
      data-testid="hero"
    >
      <div className="blueprint-grid absolute inset-0 opacity-40" />
      <div className="pointer-events-none absolute -right-40 top-10 h-[600px] w-[600px] rounded-full bg-gold/[0.06] blur-[130px]" />

      <div className="relative z-10 mx-auto grid max-w-[1440px] items-center gap-12 px-5 py-16 md:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:py-24">
        <motion.div style={{ y: yText, opacity: fade }}>
          <div className="mb-6 flex items-center gap-3 font-heading text-xs font-bold uppercase tracking-[0.2em] text-gold">
            <span className="h-px w-8 bg-gold" />
            Safe &amp; Vault Specialists · {business.city}, {business.state}
          </div>

          <h1 className="font-heading text-5xl font-black uppercase leading-[0.92] tracking-tightest text-white sm:text-6xl lg:text-7xl">
            <MaskReveal
              delay={0.15}
              lines={[
                "Safe & Vault",
                "Expertise You",
                <span key="ct" className="text-gold">
                  Can Trust
                </span>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-7 max-w-lg text-lg leading-relaxed text-steel"
          >
            Professional safe and vault services — from safe lockouts and lock
            repairs to difficult, heavy and high-security safe work throughout{" "}
            {business.region}.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.82, duration: 0.7 }}
            className="mt-5 space-y-2"
          >
            <p className="font-heading text-sm font-bold uppercase tracking-[0.16em] text-gold sm:text-base">
              Safe Moving <span className="text-steel">•</span> Safe Removal{" "}
              <span className="text-steel">•</span> Safe Disposal
            </p>
            <p className="font-heading text-xs font-bold uppercase tracking-[0.22em] text-steel-light">
              {business.credentials}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <CallButton size="lg" showNumber testid="hero-call-btn" />
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="group inline-flex h-14 items-center gap-2 rounded-sm border border-white/25 px-7 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-300 hover:bg-white/10"
            >
              View Services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: yMedia }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <MediaPlaceholder
            src="/media/hero-safe-trailer.jpg"
            alt="Velasco Lock and Safe truck and trailer transporting a large safe"
            aspect="aspect-[4/3]"
            priority
          />
          <div className="absolute bottom-4 left-4 z-10 flex items-center gap-3 border border-white/10 bg-ink/90 px-4 py-3 backdrop-blur-xl sm:px-5 sm:py-4">
            <ShieldCheck className="h-7 w-7 shrink-0 text-gold sm:h-8 sm:w-8" />
            <div>
              <div className="font-heading text-sm font-bold text-white">
                Difficult Safe Jobs Welcome
              </div>
              <div className="text-xs text-steel">All types of safes evaluated</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
